import { fileNameMap, publicationUploadMaxSize } from 'src/constants';
import {
  PDF_MERGE_LIST,
  PDF_MERGE_MAKE_MERGE,
  PDF_MERGE_MERGE_STATUS,
} from 'src/redux/constants';
import { calcMergeStatus, getActionAPISource } from 'src/utils';
import type {
  AcrFileColumn,
  CommentOrdering,
  FileValidationContext,
  NarrativeLayout,
  NarrativeRefreshContext,
  NarrativeRefreshType,
  PublicationReport,
  PublicationResultColumn,
  PublicationStatus,
  WorkspaceVariant,
} from './types';

export abstract class CrfVersion {
  abstract readonly code: string;
  abstract get workspaceVariant(): WorkspaceVariant;
  abstract get narrativeLayout(): NarrativeLayout;
  abstract get commentOrdering(): CommentOrdering;
  abstract get viewCommentsLabel(): string;

  narrativeRefreshType(ctx: NarrativeRefreshContext): NarrativeRefreshType {
    if (this.narrativeLayout === 'multi-narrative') return 'multi';
    if (ctx.isLinkedMainNarrative) return 'main';
    if (ctx.isLinkedExtraNarrative) return 'extra';
    return '';
  }

  get fileColumns(): AcrFileColumn[] {
    return [
      { type: 'acr_5', header: 'ACR 5', accessor: 'acr_5_file_url' },
      { type: 'acr_1', header: 'ACR 1', accessor: 'acr_1_file_url' },
    ];
  }

  get resultColumns(): PublicationResultColumn[] {
    return [
      { kind: 'submitted', header: 'Report Submitted' },
      { kind: 'reportLink', header: 'Report Link' },
    ];
  }

  get statusColumnHeader(): string {
    return 'Merge Status';
  }

  get actionLabel(): string {
    return 'Merge ready';
  }

  get actionTestId(): string {
    return 'merge-ready-button';
  }

  get actionStartedResponse(): string {
    return 'Merge started';
  }

  get actionStartedMessage(): string {
    return 'Merge started';
  }

  get actionCompletedMessage(): string {
    return 'Merge completed';
  }

  get pendingFlagKey(): string {
    return 'waiting_merge_result';
  }

  get confirmationHeading(): string {
    return 'Confirm Merge';
  }

  get confirmationIntro(): string {
    return 'These ACR previews will be generated:';
  }

  get confirmationQuestion(): string {
    return 'Do you want to proceed with merge?';
  }

  listUrl(): string {
    return getActionAPISource(PDF_MERGE_LIST);
  }

  startActionUrl(): string {
    return getActionAPISource(PDF_MERGE_MAKE_MERGE);
  }

  statusUrl(): string {
    return getActionAPISource(PDF_MERGE_MERGE_STATUS);
  }

  uploadUrl(): string {
    return this.listUrl();
  }

  deleteUrl(pk: string | number, type: string): string {
    return `${this.listUrl()}${pk}/${type}`;
  }

  calcStatus(report: PublicationReport): PublicationStatus {
    return calcMergeStatus(report) as PublicationStatus;
  }

  validateFile(
    file: File,
    {
      projectsCodesList,
      doneProjectsCodesList,
      expectedType,
    }: FileValidationContext,
  ): string {
    const fileFormat = file?.name?.split('.').pop();
    const fileName = file?.name?.split('.').shift();

    if (fileFormat !== 'pdf') return 'file_type';

    if (file?.size / 1024 / 1024 > publicationUploadMaxSize) return 'file_size';

    if (!fileName.includes(' ')) return 'file_name';

    const fileNameSplitted = fileName.split(' ');
    if (!projectsCodesList.includes(fileNameSplitted[0])) {
      return 'file_name_project_id';
    }

    if (doneProjectsCodesList.includes(fileNameSplitted[0])) {
      return 'file_name_already_merged';
    }

    if (!fileNameSplitted[1].includes('-')) return 'file_name';

    const secondPartSplitted = fileNameSplitted[1].split('-');
    const allowedPrefixes = expectedType
      ? [fileNameMap[expectedType]]
      : this.fileColumns.map(({ type }) => fileNameMap[type]);
    if (!allowedPrefixes.includes(secondPartSplitted[0])) return 'file_name';

    if (secondPartSplitted[1] !== 'B') return 'file_name';

    return '';
  }
}
